/**
 * Enhanced useGuests Hook with improved type safety and caching
 * 
 * Provides data and operations for managing podcast guests with proper error handling,
 * type safety, and cache invalidation for optimal performance.
 */
import { useCallback } from 'react';
import { useApi } from '@/shared/hooks/useApi';
import guestsService from '@/services/api/guests';
import type { Guest } from '@/types/models/schema';
import type { ApiResponse } from '@/types/api';
import { AxiosInstance } from 'axios';
import { useError } from '@/shared/contexts/ErrorContext';

/**
 * Return type for useGuests hook
 * 
 * Defines the complete interface for all data and operations provided by the useGuests hook,
 * ensuring type safety and consistent API usage throughout the application.
 */
export interface UseGuestsReturn {
  /** API response containing guests data */
  guests: ApiResponse<Guest[]>;
  /** Loading state indicator */
  isLoading: boolean;
  /** Error object if request failed */
  error: Error | null;
  /** Function to fetch a guest by ID */
  getGuestById: (id: string) => Promise<ApiResponse<Guest>>;
  /** Alias for getGuestById for backward compatibility */
  getGuest: (id: string) => Promise<ApiResponse<Guest>>;
  /** Function to create a new guest */
  createGuest: (data: Omit<Guest, 'id' | 'created_at'>) => Promise<ApiResponse<Guest>>;
  /** Function to update a guest */
  updateGuest: (id: string, data: Partial<Guest>) => Promise<ApiResponse<Guest>>;
  /** Function to delete a guest */
  deleteGuest: (id: string) => Promise<ApiResponse<boolean>>;
  /** Function to refetch guests */
  refetchGuests: () => Promise<void>;
  /** Function to retry failed request */
  retryFetch: () => void;
}

/**
 * Hook for managing podcast guests
 * 
 * Provides a comprehensive interface for fetching, creating, updating, and deleting podcast guests.
 * Includes built-in error handling, caching, and type safety.
 * 
 * @returns {UseGuestsReturn} Guest data and operations
 */
export function useGuests(): UseGuestsReturn {
  const { addError } = useError();
  
  // Use the updated useApi hook pattern with correct signature for raw data
  const {
    data: guests,
    isLoading,
    error,
    refetch,
    retry,
    invalidateCache
  } = useApi<Guest[]>(
    useCallback(async (client: AxiosInstance) => {
      // Ensure we return an empty array instead of null for type safety
      const result = await guestsService.createGuestsService(addError).getGuests();
      return result || [];
    }, [addError])
  );
  
  const getGuestById = useCallback(async (id: string): Promise<ApiResponse<Guest>> => {
    const rawResponse = await guestsService.createGuestsService(addError).getGuestById(id);
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<Guest> = {
      data: rawResponse,
      error: rawResponse === null ? new Error(`Failed to fetch guest with ID ${id}`) : null
    };
    
    return response;
  }, [addError]);
  
  // Alias for backward compatibility
  const getGuest = getGuestById;
  
  const createGuest = useCallback(async (data: Omit<Guest, 'id' | 'created_at'>): Promise<ApiResponse<Guest>> => {
    const rawResponse = await guestsService.createGuestsService(addError).createGuest(data);
    invalidateCache();
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<Guest> = {
      data: rawResponse,
      error: rawResponse === null ? new Error('Failed to create guest') : null
    };
    
    return response;
  }, [addError, invalidateCache]);
  
  const updateGuest = useCallback(async (id: string, data: Partial<Guest>): Promise<ApiResponse<Guest>> => {
    const rawResponse = await guestsService.createGuestsService(addError).updateGuest(id, data);
    invalidateCache();
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<Guest> = {
      data: rawResponse,
      error: rawResponse === null ? new Error(`Failed to update guest with ID ${id}`) : null
    };
    
    return response;
  }, [addError, invalidateCache]);
  
  const deleteGuest = useCallback(async (id: string): Promise<ApiResponse<boolean>> => {
    const rawResponse = await guestsService.createGuestsService(addError).deleteGuest(id);
    invalidateCache();
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<boolean> = {
      data: rawResponse,
      error: rawResponse === false ? new Error(`Failed to delete guest with ID ${id}`) : null
    };
    
    return response;
  }, [addError, invalidateCache]);
  
  return {
    guests,
    isLoading,
    error,
    getGuestById,
    getGuest,
    createGuest,
    updateGuest,
    deleteGuest,
    refetchGuests: refetch,
    retryFetch: retry
  };
}

export default useGuests;
